import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CalendarDays, MapPin, Trophy, Swords } from "lucide-react";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function History() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | internal | official

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches");
        setMatches(data.filter((m) => m.status === "finished"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const internal = matches.filter((m) => (m.match_type || "internal") === "internal");
  const official = matches.filter((m) => m.match_type === "official");

  const bleusWins = internal.filter((m) => (m.score_bleus ?? 0) > (m.score_rouges ?? 0)).length;
  const rougesWins = internal.filter((m) => (m.score_rouges ?? 0) > (m.score_bleus ?? 0)).length;
  const draws = internal.filter((m) => (m.score_bleus ?? 0) === (m.score_rouges ?? 0)).length;

  const officialWins = official.filter((m) => (m.our_score ?? 0) > (m.opponent_score ?? 0)).length;
  const officialLoss = official.filter((m) => (m.our_score ?? 0) < (m.opponent_score ?? 0)).length;
  const officialDraws = official.filter((m) => (m.our_score ?? 0) === (m.opponent_score ?? 0)).length;

  const filtered =
    filter === "all"
      ? matches
      : matches.filter((m) => (m.match_type || "internal") === filter);

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
          Archives
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
          Historique
        </h1>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <Swords className="w-4 h-4 text-slate-400" />
          <div className="text-[11px] font-mono-stat uppercase tracking-widest text-slate-400">
            Duels internes (Bleus vs Rouges)
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <RecordCard label="Victoires Bleus" value={bleusWins} color="#60A5FA" testid="record-bleus" />
          <RecordCard label="Nuls" value={draws} color="#94A3B8" testid="record-draws" />
          <RecordCard label="Victoires Rouges" value={rougesWins} color="#DC2626" testid="record-rouges" />
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4 text-[#E5B85C]" />
          <div className="text-[11px] font-mono-stat uppercase tracking-widest text-[#E5B85C]">
            Matchs officiels
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <RecordCard label="Victoires" value={officialWins} color="#22C55E" testid="record-off-wins" />
          <RecordCard label="Nuls" value={officialDraws} color="#94A3B8" testid="record-off-draws" />
          <RecordCard label="Défaites" value={officialLoss} color="#EF4444" testid="record-off-loss" />
        </div>
      </div>

      <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-900 border border-white/10 w-fit">
        {[
          { key: "all", label: "Tous" },
          { key: "internal", label: "Internes" },
          { key: "official", label: "Officiels" },
          { key: "gala", label: "Gala" },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`history-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide ${
              filter === f.key
                ? "bg-[#E5B85C] text-[#0B0F17]"
                : "text-slate-300 hover:bg-slate-800"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 rounded-2xl border border-dashed border-white/10 text-center text-slate-500">
          Aucun match terminé
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => {
            const isOfficial = m.match_type === "official";
            const isGala = m.match_type === "gala";
            const badgeLabel = isOfficial ? "Officiel" : isGala ? "Gala" : "Interne";
            const badgeClass = isOfficial
              ? "bg-[#E5B85C]/20 text-[#E5B85C] border border-[#E5B85C]/40"
              : isGala
              ? "bg-purple-500/20 text-purple-300 border border-purple-500/40"
              : "bg-slate-800 text-slate-400 border border-white/10";
            return (
              <Link
                key={m.id}
                to={`/matches/${m.id}`}
                data-testid={`history-row-${m.id}`}
                className="block p-4 rounded-xl bg-slate-900 border border-white/10 hover:border-[#E5B85C]/40"
              >
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[9px] font-mono-stat uppercase tracking-widest px-1.5 py-0.5 rounded ${badgeClass}`}
                      >
                        {badgeLabel}
                      </span>
                      <div className="font-display font-bold truncate">
                        {isOfficial
                          ? `${m.title || "Match"} · vs ${m.opponent_name || "Adversaire"}`
                          : isGala
                          ? `${m.title || "Match de gala"} · ${m.team_a_name || "Équipe A"} vs ${m.team_b_name || "Équipe B"}`
                          : m.title || "Match ASS les Régionnaires"}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-400 font-mono-stat mt-1">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="w-3 h-3" /> {formatDate(m.match_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {m.location}
                      </span>
                    </div>
                  </div>
                  <div className="font-mono-stat font-bold text-lg sm:text-2xl flex items-center gap-2">
                    {isOfficial ? (
                      <>
                        <span className="text-[#E5B85C]">Nous {m.our_score ?? 0}</span>
                        <span className="text-slate-500">-</span>
                        <span className="text-slate-300">
                          {m.opponent_score ?? 0} {m.opponent_name || "Adv."}
                        </span>
                      </>
                    ) : isGala ? (
                      <>
                        <span className="text-purple-300">{m.team_a_name || "A"} {m.score_team_a ?? 0}</span>
                        <span className="text-slate-500">-</span>
                        <span className="text-fuchsia-300">{m.score_team_b ?? 0} {m.team_b_name || "B"}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-sky-400">Bleus {m.score_bleus ?? 0}</span>
                        <span className="text-slate-500">-</span>
                        <span className="text-rose-400">{m.score_rouges ?? 0} Rouges</span>
                      </>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RecordCard({ label, value, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-5 rounded-xl bg-slate-900 border border-white/10 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: color }} />
      <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div className="font-display font-black text-4xl mt-2" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
